import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import styled from "styled-components"
import GridContainer from "@/components/common/GridContainer"
import Text from "@/components/common/Text"
import { fetcher, getCssSizeValue, getSectionDataFromObject, isUndefined } from "@/utils/utils"
import useSWRMutation from "swr/mutation"
import CommonIconButton from "@/components/common/Buttons/IconButton"
import TextInput from "@/components/common/TextInput"
import { BREAKPOINTS } from "@/styles/themeSettings"
import Fundraiser from "@/components/Fundraisers/Fundraiser"
import { ICONS } from "@/constants"
import useScrollContext from "@/hooks/useScrollContext"
import useGlobals from "@/hooks/useGlobals"
import Icon from "@/components/common/Icon/Icon"

const Wrapper = styled(GridContainer)`
	padding-top: ${getCssSizeValue(40, BREAKPOINTS.MOBILE)};
	padding-bottom: ${getCssSizeValue(40, BREAKPOINTS.MOBILE)};
	background-color: ${({ theme }) => theme.colors.platinum};
	row-gap: ${getCssSizeValue(20, BREAKPOINTS.MOBILE)};

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		padding-top: ${getCssSizeValue(80, BREAKPOINTS.TABLET)};
		padding-bottom: ${getCssSizeValue(40, BREAKPOINTS.TABLET)};
		row-gap: ${getCssSizeValue(25, BREAKPOINTS.TABLET)};
	}

	@media (min-width: ${({ theme }) => theme.breakpoints.desktop}px) and (orientation: landscape) {
		padding-top: ${getCssSizeValue(80)};
		padding-bottom: ${getCssSizeValue(40)};
		row-gap: var(--grid-gap);
	}
`

const Container = styled(GridContainer)`
	row-gap: ${getCssSizeValue(20, BREAKPOINTS.MOBILE)};
	align-items: center;

	.hint {
		display: flex;
		align-items: center;

		.icon {
			width: ${getCssSizeValue(22, BREAKPOINTS.MOBILE)};
			margin-right: 8px;
		}
	}

	&.search {
		grid-template-columns: repeat(var(--size), 1fr);
	}

	&.purple {
		min-height: 50px;
		background-color: ${({ theme }) => theme.colors.red};
	}

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		.hint {
			.icon {
				width: ${getCssSizeValue(22, BREAKPOINTS.TABLET)};
			}
		}

		row-gap: ${getCssSizeValue(25, BREAKPOINTS.TABLET)};
	}

	@media (min-width: ${({ theme }) => theme.breakpoints.desktop}px) and (orientation: landscape) {
		.hint {
			.icon {
				width: ${getCssSizeValue(22, BREAKPOINTS.DESKTOP)};
			}
		}

		row-gap: ${getCssSizeValue(50)};
	}
`

const IconButton = styled(CommonIconButton)`
	.icon {
		width: ${getCssSizeValue(20, BREAKPOINTS.MOBILE)};
	}

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		.icon {
			width: ${getCssSizeValue(20, BREAKPOINTS.TABLET)};
		}
	}

	@media (min-width: ${({ theme }) => theme.breakpoints.desktop}px) and (orientation: landscape) {
		.icon {
			width: ${getCssSizeValue(20)};
		}
	}
`

function useFundraiser(id) {
	return useSWRMutation(
		["/api/getFundraiser", { id: id.toLowerCase() }],
		// ['/api/getFundraiser', { id }],
		fetcher,
		{ revalidateOnFocus: false, revalidateOnMount: false, populateCache: true },
	)
}

const DEFAULT_FUNDRAISER = {
	exist: undefined,
}

export function useFundraisersData() {
	return useGlobals((state) => getSectionDataFromObject(state, "fundraisers"))
}

const Fundraisers = () => {
	const [fundraiserId, setFundraiserId] = useState("")
	const [currentFundraiser, setCurrentFundraiser] = useState(DEFAULT_FUNDRAISER)
	const InputRef = useRef()
	const { isMutating, trigger } = useFundraiser(fundraiserId)
	const searchAllowed = useMemo(() => fundraiserId.length <= 0, [fundraiserId])
	const hasResult = useMemo(() => !isUndefined(currentFundraiser.exist), [currentFundraiser])
	const scroll = useScrollContext()
	const { inputPlaceholder, header, searchButtonText, hint } = useFundraisersData()
	const handleSearchButtonClick = useCallback(async () => {
		try {
			const result = await trigger()
			let curr
			if (result === null) {
				curr = {
					exist: false,
				}
			} else {
				curr = {
					exist: true,
					...result,
				}
			}
			setCurrentFundraiser(curr)
		} catch (error) {
			console.error(error.message)
		}
	}, [trigger])

	const handleOnInputChange = useCallback((e) => {
		setFundraiserId(e.target.value)
	}, [])

	useEffect(() => {
		//update scroll triggers
		scroll.refresh()
	}, [scroll, hasResult])

	return (
		<Wrapper className="no-margin padding indent-tablet-1 indent-desktop-2">
			<Container className="search col-mobile-8 col-tablet-7 col-desktop-14 no-margin">
				<Text id="fundraiser-label" className="typo-borna-89 col-mobile-8 col-tablet-7 col-desktop-14">
					{header}
				</Text>
				<TextInput
					ref={InputRef}
					name="Fundraiser search"
					value={fundraiserId}
					onChange={handleOnInputChange}
					containerClassName="col-mobile-8 col-tablet-4 col-desktop-6"
					aria-labelledby="fundraiser-label"
					placeholder={inputPlaceholder}
					disabled={hasResult}
				/>
				{!hasResult && !isMutating ? (
					<IconButton
						onClick={handleSearchButtonClick}
						className="col-mobile-8 col-tablet-2 col-desktop-2"
						disabled={searchAllowed}
					>
						{searchButtonText}
					</IconButton>
				) : (
					<IconButton
						icon={ICONS.CLOSE}
						onClick={() => {
							setFundraiserId("")
							setCurrentFundraiser(DEFAULT_FUNDRAISER)
						}}
						className="col-mobile-8 col-tablet-2 col-desktop-2"
					/>
				)}
				{hint?.length >= 0 && (
					<Text tag="div" className="typo-borna-33 col-mobile-7 col-tablet-7 col-desktop-13 hint">
						<Icon name={ICONS.EXCLAMATION_MARK} />
						{hint}
					</Text>
				)}
			</Container>
			{hasResult && (
				<Fundraiser data={currentFundraiser} className="col-mobile-8 col-desktop-16 no-margin indent-tablet-1 indent-desktop-2" />
			)}
		</Wrapper>
	)
}

export default Fundraisers
